import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import style from './AnalyzeDocument.style';
import { DocumentStatus, ScorecardStatus } from '@Constants/status';
import MiddleInfoMessage from '@Components/UI/MiddleInfoMessage';
import { cpu, document, checkboxState } from '@Components/UI/AnimatedSvgs';
import * as commonUtils from '@Utils/commonUtils';
import { STORAGE_TYPE, setInStorage } from '@Service/storageService';
import { layouts } from 'seal-ui-components';
import StartPage from './StartPage';
import store from '@Store';
import { MIX_PANEL } from '@Constants/types';
import mixpanelEvents from '@Mixpanel';

const AnalyzeDocument = ({
    className,
    documentStatus,
    scorecardStatus,
    children,
    statues,
    pageType,
    data,
    run,
    abort,
    cancel,
    stopAnimation,
    resetAnimation
}) => {
    const ANIMATION_CLASSES = {
        pushUp: 'push-up',
        pushDown: 'push-down',
        fadeIn: 'fade-in',
        fadeOut: 'fade-out',
        morphGates: 'morph-gates',
        openGates: 'open',
        closeGates: 'close'
    };

    const initialState = {
        middleInfoMessageAnimation: '',
        middleInfoMessage: <StartPage />,
        mainSvgFrameAnimation: '',
        mainContainerAnimation: '',
        docSvgFrameAnimation: '',
        cpuAnimation: ANIMATION_CLASSES.closeGates,
        docTrigger: false,
        statusTrigger: false,
        currentDocStatus: DocumentStatus.NOT_SUBMITTED,
        currentScorecardStatus: ScorecardStatus.NOT_ANALYZED
    };
    const [
        {
            middleInfoMessageAnimation,
            middleInfoMessage,
            mainSvgFrameAnimation,
            mainContainerAnimation,
            docSvgFrameAnimation,
            cpuAnimation,
            docTrigger,
            statusTrigger,
            currentDocStatus,
            currentScorecardStatus
        },
        setState
    ] = useState(initialState);

    const [tooMuchTimeInitializingAI, setTooMuchTimeInitializingAI] = useState(false);
    const [tooMuchTimeAnalyzingDoc, setTooMuchTimeAnalyzingDoc] = useState(false);
    const [tooMuchTimeGeneratingScorecards, setTooMuchTimeGeneratingScorecards] = useState(false);
    const [initializationTimeout, setInitializationTimeout] = useState(false);
    const [analyzingDocTimeout, setAnalyzingDocTimeout] = useState(false);
    const [scorecardsTimeout, setScorecardsTimeout] = useState(false);
    const isIE11 = commonUtils.isIE11Browser();

    const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

    const clearStates = () => {
        resetAnimation();
        setState({ ...initialState });
        setTooMuchTimeInitializingAI(false);
        setTooMuchTimeAnalyzingDoc(false);
        setTooMuchTimeGeneratingScorecards(false);
        setInitializationTimeout(false);
        setAnalyzingDocTimeout(false);
        setScorecardsTimeout(false);
    };

    useEffect(
        () => {
            if (cancel) {
                (async () => {
                    setState(prevState => ({
                        ...prevState,
                        middleInfoMessage: 'Analysis cancelled',
                        middleInfoMessageAnimation: ANIMATION_CLASSES.pushUp
                    }));
                    setInStorage('sealAtnDocumentStatus', '', STORAGE_TYPE.PROPERTY_BAG);
                    await sleep(3000);
                    clearStates();
                    setState(prevState => ({
                        ...prevState,
                        middleInfoMessageAnimation: ANIMATION_CLASSES.fadeIn
                    }));
                })();
            }
            if (abort) {
                setInStorage('sealAtnDocumentStatus', '', STORAGE_TYPE.PROPERTY_BAG);
                clearStates();
            }
        },
        [cancel, abort]
    );

    useEffect(
        () => {
            /** show/hide warning message if AI initialization takes too much time */
            if (tooMuchTimeInitializingAI && currentDocStatus === DocumentStatus.INITIALIZING) {
                setState(prevState => ({
                    ...prevState,
                    middleInfoMessage:
                        'The server is currently busy, your document will be processed as soon as possible',
                    middleInfoMessageAnimation: ANIMATION_CLASSES.pushUp
                }));
            }
            if (
                tooMuchTimeInitializingAI &&
                initializationTimeout &&
                currentDocStatus === DocumentStatus.INITIALIZING
            ) {
                setState(prevState => ({
                    ...prevState,
                    middleInfoMessageAnimation: ANIMATION_CLASSES.pushDown
                }));
                sleep(500).then(() => {
                    setState(prevState => ({
                        ...prevState,
                        middleInfoMessage: 'Server is busy, please wait or retry submission',
                        middleInfoMessageAnimation: ANIMATION_CLASSES.pushUp
                    }));
                });
            }

            /** show/hide warning message if document analysis takes too much time */
            if (tooMuchTimeAnalyzingDoc && currentDocStatus === DocumentStatus.PENDING) {
                setState(prevState => ({
                    ...prevState,
                    middleInfoMessage: 'Nearly done...',
                    middleInfoMessageAnimation: ANIMATION_CLASSES.pushUp
                }));
            }
            if (
                tooMuchTimeAnalyzingDoc &&
                analyzingDocTimeout &&
                currentDocStatus === DocumentStatus.PENDING
            ) {
                setState(prevState => ({
                    ...prevState,
                    middleInfoMessageAnimation: ANIMATION_CLASSES.pushDown
                }));
                sleep(500).then(() => {
                    setState(prevState => ({
                        ...prevState,
                        middleInfoMessage: 'Server is busy, please wait or retry submission',
                        middleInfoMessageAnimation: ANIMATION_CLASSES.pushUp
                    }));
                });
            }

            /** show/hide warning message if scorecard generation takes too much time */
            if (
                tooMuchTimeGeneratingScorecards &&
                currentScorecardStatus === ScorecardStatus.PENDING
            ) {
                setState(prevState => ({
                    ...prevState,
                    middleInfoMessage: 'Nearly done...',
                    middleInfoMessageAnimation: ANIMATION_CLASSES.pushUp
                }));
            }
            if (
                tooMuchTimeGeneratingScorecards &&
                scorecardsTimeout &&
                currentScorecardStatus === ScorecardStatus.PENDING
            ) {
                setState(prevState => ({
                    ...prevState,
                    middleInfoMessageAnimation: ANIMATION_CLASSES.pushDown
                }));
                sleep(500).then(() => {
                    setState(prevState => ({
                        ...prevState,
                        middleInfoMessage: 'Server is busy, please wait or retry submission',
                        middleInfoMessageAnimation: ANIMATION_CLASSES.pushUp
                    }));
                });
            }
        },
        [
            tooMuchTimeInitializingAI,
            tooMuchTimeAnalyzingDoc,
            tooMuchTimeGeneratingScorecards,
            initializationTimeout,
            analyzingDocTimeout,
            scorecardsTimeout,
            currentDocStatus,
            currentScorecardStatus
        ]
    );

    useEffect(
        () => {
            if (run) {
                if (documentStatus === DocumentStatus.INITIALIZING) {
                    sleep(0)
                        .then(() => {
                            // push down 'Click ANALYZE to analyze' message
                            setState(prevState => ({
                                ...prevState,
                                middleInfoMessageAnimation: ANIMATION_CLASSES.pushDown
                            }));
                            if (!isIE11) {
                                setState(prevState => ({
                                    ...prevState,
                                    mainSvgFrameAnimation: ''
                                }));
                            }

                            return sleep(1000);
                        })
                        .then(() => {
                            // fade-in checkbox states
                            setState(prevState => ({
                                ...prevState,
                                statusTrigger: true
                            }));
                            return sleep(statues.length * 500);
                        })
                        .then(() => {
                            setState(prevState => ({
                                ...prevState,
                                currentDocStatus: documentStatus
                            }));
                            if (isIE11) {
                                // fade in main frame of svgs
                                setState(prevState => ({
                                    ...prevState,
                                    docSvgFrameAnimation: ANIMATION_CLASSES.fadeIn,
                                    docTrigger: true
                                }));
                            } else {
                                // fade in main frame of svgs
                                setState(prevState => ({
                                    ...prevState,
                                    mainSvgFrameAnimation: ANIMATION_CLASSES.fadeIn
                                }));
                            }

                            return sleep(500);
                        })
                        .then(() => {
                            // change doc status
                            // open main frame animation
                            setState(prevState => ({
                                ...prevState,
                                cpuAnimation: ANIMATION_CLASSES.openGates
                            }));
                        });
                }
                if (documentStatus === DocumentStatus.PENDING) {
                    setState(prevState => ({
                        ...prevState,
                        middleInfoMessageAnimation: ANIMATION_CLASSES.pushDown
                    }));
                    sleep(8000)
                        .then(() => {
                            // close main frame animation
                            setState(prevState => ({
                                ...prevState,
                                cpuAnimation: ANIMATION_CLASSES.closeGates
                            }));
                            return sleep(500);
                        })
                        .then(() => {
                            if (!isIE11) {
                                // morph main frame animation
                                // change doc status
                                // push down server loaded message
                                setState(prevState => ({
                                    ...prevState,
                                    mainSvgFrameAnimation: ANIMATION_CLASSES.morphGates
                                }));
                            }
                            return sleep(500);
                        })
                        .then(() => {
                            setState(prevState => ({
                                ...prevState,
                                currentDocStatus: documentStatus
                            }));
                            if (!isIE11) {
                                setState(prevState => ({
                                    ...prevState,
                                    docSvgFrameAnimation: ANIMATION_CLASSES.fadeIn,
                                    docTrigger: true
                                }));
                            }
                        });
                }
                if (documentStatus === DocumentStatus.PROCESSED) {
                    // pull down "Hang tight, almost done" message for doc
                    setState(prevState => ({
                        ...prevState,
                        middleInfoMessageAnimation: ANIMATION_CLASSES.pushDown
                    }));
                    if (pageType === 'labels') {
                        sleep(1000)
                            .then(() => {
                                setState(prevState => ({
                                    ...prevState,
                                    currentDocStatus: documentStatus,
                                    middleInfoMessage: 'All done!',
                                    middleInfoMessageAnimation: ANIMATION_CLASSES.pushUp
                                }));
                                return sleep(3500);
                            })
                            .then(() => {
                                setState(prevState => ({
                                    ...prevState,
                                    mainContainerAnimation: ANIMATION_CLASSES.fadeOut
                                }));
                                return sleep(250);
                            })
                            .then(() => {
                                // end of the animation
                                stopAnimation();
                                clearStates();
                            });
                    }
                    if (pageType === 'scorecard') {
                        setState(prevState => ({
                            ...prevState,
                            currentDocStatus: documentStatus
                        }));
                    }
                }

                if (scorecardStatus === ScorecardStatus.PENDING) {
                    setState(prevState => ({
                        ...prevState,
                        currentScorecardStatus: scorecardStatus
                    }));
                }
                if (
                    scorecardStatus === ScorecardStatus.PROCESSED &&
                    documentStatus === DocumentStatus.PROCESSED
                ) {
                    // pull down "Hang tight, almost done" message for scorecard
                    setState(prevState => ({
                        ...prevState,
                        middleInfoMessageAnimation: ANIMATION_CLASSES.pushDown
                    }));
                    sleep(1000)
                        .then(() => {
                            store.dispatch({
                                type: MIX_PANEL.MIXPANEL_DATA_SCORECARD_STEP_TIME,
                                payload: 'completion'
                            });
                            mixpanelEvents.generatingScorecardStepComplete();
                            setState(prevState => ({
                                ...prevState,
                                middleInfoMessage: 'All done!',
                                currentScorecardStatus: scorecardStatus,
                                middleInfoMessageAnimation: ANIMATION_CLASSES.pushUp
                            }));

                            return sleep(3500);
                        })
                        .then(() => {
                            setState(prevState => ({
                                ...prevState,
                                mainContainerAnimation: ANIMATION_CLASSES.fadeOut
                            }));
                            return sleep(250);
                        })
                        .then(() => {
                            // end of the animation
                            stopAnimation();
                            clearStates();
                        });
                }
            }
        },
        [documentStatus, scorecardStatus, run]
    );
    if (data !== null && typeof data !== 'undefined' && !commonUtils.isEmptyObj(data) && !run)
        return children;

    if (
        scorecardStatus === ScorecardStatus.PENDING &&
        documentStatus !== DocumentStatus.NOT_SUBMITTED &&
        !run
    ) {
        return null;
    }

    return (
        <div className={`analyze-document ${className} ${mainContainerAnimation}`}>
            <div id="analyze-document-top" className="analyze-document-top">
                {run && (
                    <>
                        <div
                            id="analyze-document-top-init-step-id"
                            className={`analyze-document-top-init-step ${mainSvgFrameAnimation}`}
                        >
                            {!isIE11 && <cpu.Cpu gateStatus={cpuAnimation} />}
                        </div>
                        <div
                            id="analyze-document-top-document-step-id"
                            className={`analyze-document-top-document-step ${docSvgFrameAnimation}`}
                        >
                            <document.Document
                                page={pageType}
                                start={docTrigger && run}
                                documentStatus={currentDocStatus}
                                scorecardStatus={currentScorecardStatus}
                                isIE11={isIE11}
                            />
                        </div>
                    </>
                )}
                <MiddleInfoMessage
                    noposition="false"
                    message={middleInfoMessage}
                    className={middleInfoMessageAnimation}
                />
            </div>
            <div id="analyze-document-bottom" className="analyze-document-bottom">
                <layouts.VerticalBoxDivider className="divider" />
                <checkboxState.CheckboxState
                    page={pageType}
                    labels={statues}
                    start={statusTrigger && run}
                    documentStatus={currentDocStatus}
                    scorecardStatus={currentScorecardStatus}
                    setTooMuchTimeAnalyzingDoc={setTooMuchTimeAnalyzingDoc}
                    setTooMuchTimeInitializingAI={setTooMuchTimeInitializingAI}
                    setTooMuchTimeGeneratingScorecards={setTooMuchTimeGeneratingScorecards}
                    setInitializationTimeout={setInitializationTimeout}
                    setAnalyzingDocTimeout={setAnalyzingDocTimeout}
                    setScorecardsTimeout={setScorecardsTimeout}
                />
            </div>
        </div>
    );
};

export default styled(AnalyzeDocument)`
    ${style};
`;
